import { useTranslation } from "react-i18next";
import { SetStateAction, useEffect, useState } from "react";
import {
	LicenseDesignation,
	LicenseType,
	Severity,
	WeightUnit,
} from "node-library/dist/constants";

import { createUUID } from "node-library/dist/helpers/request_helper";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import {
	CustomDropdown,
	CustomTextInput,
} from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import { GreenButton } from "../../../../common-components/button";
import { FrontendOrganizationLicense } from "node-library/dist/portal";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	organizationId: string;
	testCategoryId: string;
	addLicense: (license: FrontendOrganizationLicense) => void;
}

export default function AddLicenseModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { addLicense, open, setOpen, organizationId, testCategoryId } = props;

	const [loading, setLoading] = useState<boolean>(false);

	const [licenseDesignation, setLicenseDesignation] =
		useState<LicenseDesignation>(LicenseDesignation.MEDICAL);
	const [licenseType, setLicenseType] = useState<LicenseType>(
		LicenseType.LICENSED_PRODUCER
	);
	const [licenseNumber, setLicenseNumber] = useState<string>("");

	//validation
	const [licenseNumberErrorMsg, setLicenseNumberErrorMsg] =
		useState<string>("");

	useEffect(() => {
		if (open) {
			setLicenseDesignation(LicenseDesignation.MEDICAL);
			setLicenseType(LicenseType.LICENSED_PRODUCER);
			setLicenseNumber("");
			setLicenseNumberErrorMsg("");
		}
	}, [open]);

	async function submitAddLicense() {
		setLicenseNumberErrorMsg("");
		if (!licenseNumber || licenseNumber.length === 0) {
			setLicenseNumberErrorMsg(translation("MissingInformation"));
			return;
		}

		setLoading(true);
		try {
			const response = await sendPOSTRequest("/license/add-license", {
				licenseDesignation,
				licenseType,
				licenseNumber,
				organizationId,
				testCategoryId,
			});

			const licenseId: string = response?.data?.newLicenseId;

			let newLicense: FrontendOrganizationLicense = {
				licenseId: licenseId,
				licenseDesignation: licenseDesignation,
				licenseType,
				licenseNumber,
				inserted: new Date(),
			};

			addLicense(newLicense);

			openSnackbar(
				translation("LicenseAddedSuccessfully"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			setOpen(false);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	const typeSelector: React.ReactNode = (
		<CustomDropdown
			id="license-type-dropdown"
			label={translation("LicenseType")}
			options={Object.values(LicenseType).map((value) => {
				return { value: value, title: translation(value) };
			})}
			value={licenseType}
			setValue={setLicenseType}
		></CustomDropdown>
	);

	const designationSelector: React.ReactNode = (
		<CustomDropdown
			id="designation-dropdown"
			label={translation("Designation")}
			options={Object.values(LicenseDesignation).map((value) => {
				return { value: value, title: translation(value) };
			})}
			value={licenseDesignation}
			setValue={setLicenseDesignation}
		></CustomDropdown>
	);

	const LicenseNumberInput: React.ReactNode = (
		<CustomTextInput
			id="license-number-input"
			label={`${translation("LicenseNumber")}`}
			value={licenseNumber}
			setValue={setLicenseNumber}
			placeholder={""}
			required
			errorMsg={licenseNumberErrorMsg}
			setErrorMsg={setLicenseNumberErrorMsg}
		/>
	);

	const licenseContent: React.ReactNode = (
		<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
			{typeSelector}
			{designationSelector}

			{LicenseNumberInput}
		</div>
	);

	const liceneModalModal: React.ReactNode = (
		<ModalWithButtons
			headerText={translation("AddNewLicense")}
			headerDescription={translation("AddNewLicenseDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<div
					style={{ display: "flex", flexDirection: "column", width: "100%" }}
				>
					<GreenButton
						key="add-or-update-license-button"
						id="add-or-update-license-button"
						size="flex"
						children={
							loading ? (
								<ButtonTextLoadingAnimation
									text={translation("Loading")}
								></ButtonTextLoadingAnimation>
							) : (
								translation("Add")
							)
						}
						disabled={loading}
						onClick={() => {
							submitAddLicense();
						}}
					></GreenButton>
				</div>,
			]}
		>
			{licenseContent}
		</ModalWithButtons>
	);
	return liceneModalModal;
}
